import * as React from 'react';
import {
  tokens,
  Button,
  Input,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import { applyCpfCnpjMask } from '../../../utils/validationUtils';
import PasswordStrengthIndicator from '../../../components/PasswordStrengthIndicator';

const useStyles = makeStyles({
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  title: {
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  input: {
    width: '100%',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
  },
  existsText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
  },
  button: {
    width: '100%',
    marginTop: tokens.spacingVerticalM,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
    marginTop: tokens.spacingVerticalS,
  },
  linkContainer: {
    textAlign: 'center',
    marginTop: tokens.spacingVerticalM,
  },
  link: {
    color: tokens.colorBrandForegroundLink,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: tokens.fontSizeBase300,
    '&:hover': {
      color: tokens.colorBrandForegroundLinkHover,
    },
  },
});

interface RegisterFormProps {
  getFieldProps: (field: string) => any;
  errors: Record<string, string>;
  registerLoading: boolean;
  registerError: string | null;
  emailExists: boolean;
  cpfCnpjExists: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onToggleMode: () => void;
  onEmailBlur?: () => void;
  onCpfCnpjBlur?: () => void;
  checkingEmail?: boolean;
  checkingCpfCnpj?: boolean;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  getFieldProps,
  errors,
  registerLoading,
  registerError,
  emailExists,
  cpfCnpjExists,
  onSubmit,
  onToggleMode,
  onEmailBlur,
  onCpfCnpjBlur,
  checkingEmail = false,
  checkingCpfCnpj = false,
}) => {
  const styles = useStyles();

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <Title3 className={styles.title}>
        Criar sua conta
      </Title3>

      <div>
        <Input
          id="email"
          required
          size="large"
          placeholder="E-mail"
          className={styles.input}
          {...getFieldProps('email')}
          onBlur={(e) => {
            getFieldProps('email').onBlur(e);
            onEmailBlur?.();
          }}
        />
        {errors.email && (
          <div id="email-error" className={styles.errorText}>
            {errors.email}
          </div>
        )}
        {checkingEmail && (
          <div className={styles.errorText}>
            Verificando e-mail...
          </div>
        )}
        {emailExists && !checkingEmail && (
          <div className={styles.errorText}>
            Email já cadastrado
          </div>
        )}
      </div>

      <div>
        <Input
          id="password"
          type="password"
          required
          size="large"
          placeholder="Senha"
          className={styles.input}
          {...getFieldProps('password')}
        />
        {errors.password && (
          <div id="password-error" className={styles.errorText}>
            {errors.password}
          </div>
        )}
        <PasswordStrengthIndicator
          password={getFieldProps('password').value || ''}
        />
      </div>

      <div>
        <Input
          id="cpfCnpj"
          required
          size="large"
          placeholder="CPF/CNPJ"
          className={styles.input}
          {...getFieldProps('cpfCnpj')}
          onChange={(e) => {
            const maskedValue = applyCpfCnpjMask(e.target.value);
            getFieldProps('cpfCnpj').onChange({
              ...e,
              target: { ...e.target, value: maskedValue }
            });
          }}
          onBlur={(e) => {
            getFieldProps('cpfCnpj').onBlur(e);
            onCpfCnpjBlur?.();
          }}
        />
        {errors.cpfCnpj && (
          <div id="cpfCnpj-error" className={styles.errorText}>
            {errors.cpfCnpj}
          </div>
        )}
        {checkingCpfCnpj && (
          <div className={styles.errorText}>
            Verificando CPF/CNPJ...
          </div>
        )}
        {cpfCnpjExists && !checkingCpfCnpj && (
          <div className={styles.errorText}>
            CPF/CNPJ já cadastrado
          </div>
        )}
      </div>

      <div>
        <Input
          id="razaoSocial"
          required
          size="large"
          placeholder="Razão Social"
          className={styles.input}
          {...getFieldProps('razaoSocial')}
        />
        {errors.razaoSocial && (
          <div id="razaoSocial-error" className={styles.errorText}>
            {errors.razaoSocial}
          </div>
        )}
      </div>

      <Button
        type="submit"
        appearance="primary"
        size="large"
        disabled={registerLoading}
        className={styles.button}
      >
        {registerLoading ? 'Criando conta...' : 'CRIAR CONTA'}
      </Button>

      {registerError && (
        <div className={styles.errorMessage}>
          {registerError}
        </div>
      )}

      <div className={styles.linkContainer}>
        <span
          className={styles.link}
          onClick={onToggleMode}
        >
          Já tem uma conta? Faça login.
        </span>
      </div>
    </form>
  );
};

export default RegisterForm;